/**
 * Indicateurs de ventes du Résumé, pour UNE fenêtre.
 *
 * Calculés à l'identique pour la période et ses deux références, pour que
 * chaque tuile compare des choses comparables. Une source manquante (Stripe ou
 * Meta pas encore chargé) rend `null` — jamais un total amputé d'une moitié.
 *
 *  · Total HT / TTC = kits + pianos + abonnements + autres (accessoires, port) ;
 *    kits = Shopify + Amazon + BtoB, abonnements = Stripe + vendus sur le site.
 *  · ROAS global   = CA kits HT (Shopify + Amazon) ÷ dépenses pub
 *  · ROAS payback  = (CA kits HT + MRR HT des abonnés devenus payants sur la
 *                    période, chacun au prix d'un mois) ÷ dépenses pub
 *  · ROAS blended  = (CA kits HT + kits vendus × LTV HT par acheteur) ÷ dépenses pub
 *                    — la LTV par acheteur vaut la LTV d'un abonné × le taux
 *                    d'abonnement des acheteurs : ceux qui ne s'abonnent pas
 *                    comptent pour zéro
 * Dépenses pub = Meta + Amazon Ads + Google Ads — facturées hors TVA
 * (autoliquidation), donc déjà HT. Tous les ROAS sont HT.
 * BtoB n'entre pas dans les ROAS : ces ventes ne viennent pas de la pub.
 *
 *  · MC2 sans abo = marge Piano LED + marge pianos : CA HT (kits Shopify +
 *    Amazon, pianos) − COGS (coût de revient du kit de sa période, coût des
 *    pianos) − frais Shopify Payments sur la part Shopify (% du TTC encaissé
 *    + fixe par commande, au prorata des kits + pianos) − dépenses pub.
 *    MC2 % = ÷ ce CA HT. Stripe n'encaisse que les abonnements.
 *  · MC2 avec abo = la même, plus le cash d'abonnements de la période (Stripe
 *    + vendus sur le site, chacun compté UNE fois), HT, moins TOUTES les
 *    retenues de la page Souscriptions (Sacem, Fees Stripe…) appliquées à ce
 *    cash ; MC2 % = ÷ (CA HT kits + pianos + abonnements HT).
 *  Un kit sans période de coût, ou un piano sans coût Shopify, rend la MC2
 *  `null` plutôt que gonflée.
 *  · MC3 = MC2 avec abo − branding de la période (page Frais, lissé au prorata
 *    des jours) ; MC4 = MC3 − charges fixes (page Charges, idem). Les deux
 *    taux sont ÷ le même CA HT que la MC2 avec abo.
 *  · Entonnoir (`funnel`) : CA HT (kits + pianos + abonnements) → MC1 = CA HT
 *    − COGS − frais de paiement − retenues abonnements → MC1' = MC1 − coût
 *    d'envoi × commandes (part kits + pianos) → MC2 = MC1 − pub (pas MC1' : le
 *    port est un à-côté, comme sur la page Shopify) → MC3 → MC4.
 *
 * Amazon non branché (aucune donnée) : il compte pour zéro, le reste des
 * indicateurs reste calculé — sinon le Résumé resterait vide tant qu'Amazon
 * n'est pas configuré.
 *
 * TTC : montants encaissés. HT : boutique d'après la TVA réelle des commandes
 * Shopify, abonnements TTC ÷ 1,20 (cf. `SUBSCRIPTION_VAT_PERCENT`).
 *
 * Abonnements vendus sur le site : encaissés par Shopify, mais comptés avec
 * les abonnements (`siteRevenue`). Retirés ici du CA produits — sans quoi ils
 * compteraient deux fois dans le total.
 */

import type { BtobTotals } from '@shared/types/btob';
import {
  SUBSCRIPTION_VAT_PERCENT,
  type AdsTotals,
  type AmazonAdsTotals,
  type AmazonTotals,
  type SalesTotals,
  type SubscriptionLtv,
  type SubscriptionTotals,
} from '@shared/types/sales';
import { shopPaymentFeeOf, type ShopPaymentFee, type SubscriptionDeduction } from '@shared/types/settings';

export interface SummaryInputs {
  shop: SalesTotals | undefined;
  subscriptions: SubscriptionTotals | undefined;
  ads: AdsTotals | undefined;
  amazon?: AmazonTotals;
  amazonAds?: AmazonAdsTotals;
  btob?: BtobTotals;
  googleAds?: AmazonAdsTotals;
  /** La même LTV pour toutes les fenêtres : c'est une estimation globale, pas un chiffre de période. */
  ltv?: SubscriptionLtv;
  /** Frais Shopify Payments (page Frais), appliqués à la part Shopify des kits + pianos. */
  shopPaymentFee?: ShopPaymentFee | null;
  /** Retenues de la page Souscriptions (Sacem, Fees Stripe…), appliquées au cash d'abonnements de la fenêtre. */
  subscriptionDeductions?: readonly SubscriptionDeduction[];
  /** Charges fixes de la fenêtre (page Charges, HT, au prorata des jours). `undefined` : pas encore chargées. */
  fixedCharges?: number;
  /** Branding de la fenêtre (page Frais, HT, au prorata des jours). `undefined` : pas encore chargé. */
  branding?: number;
  /** Coût d'envoi par commande (page Frais), pour MC1'. `null` : non réglé. */
  shippingCostPerOrder?: number | null;
}

/** Une marche de l'entonnoir financier : montant HT et part du CA HT. `null` : une brique manque. */
export interface FunnelStep {
  key: 'revenue' | 'mc1' | 'mc1Prime' | 'mc2' | 'mc3' | 'mc4';
  label: string;
  value: number | null;
  percent: number | null;
}

/** CA d'une catégorie, TTC et HT. */
export interface SummaryCategory {
  ttc: number;
  ht: number;
}

export interface SummaryMetrics {
  totalTtc: number | null;
  totalHt: number | null;
  /** Répartition du total : la somme des quatre redonne le total. */
  kits: SummaryCategory;
  pianos: SummaryCategory;
  subscriptions: SummaryCategory | null;
  others: SummaryCategory;
  /** Produits vendus : kits (Shopify + Amazon + BtoB), pianos, abonnements vendus sur le site. */
  kitsSold: number;
  pianosSold: number;
  siteSubscriptionsSold: number;
  /** Dépenses pub toutes plateformes (Meta + Amazon Ads + Google Ads), HT. */
  spendTotal: number | null;
  /** MER : CA HT des kits (Shopify + Amazon) ÷ dépenses pub toutes plateformes. */
  roasGlobal: number | null;
  /** CPA : dépenses pub toutes plateformes ÷ Piano LED payants (> 0 €) vendus, Shopify + Amazon. */
  cpa: number | null;
  cpaPaidKits: number;
  /**
   * ROAS par régie : achats que la régie s'attribue × prix HT moyen d'un Piano
   * LED sur Shopify dans la fenêtre ÷ dépenses de la régie. Passer par le
   * NOMBRE d'achats et notre prix, plutôt que par la valeur déclarée par la
   * régie, évite ses dollars convertis, son TTC et son port : on mesure des
   * kits. `null` sans dépense, sans kit vendu (pas de prix) ou régie absente.
   */
  roasMeta: number | null;
  roasGoogle: number | null;
  roasMetaKits: number;
  roasGoogleKits: number;
  roasKitPriceHt: number | null;
  metaSpend: number | null;
  googleSpend: number | null;
  /**
   * ROAS RÉEL par régie : CA HT des kits que la boutique a vus arriver par un
   * clic pub de la régie (UTM de la dernière visite, `adClicks`) ÷ dépenses de
   * la régie. Rien n'est revendiqué deux fois ; les clics sans UTM manquent,
   * c'est une borne basse.
   */
  roasMetaReal: number | null;
  roasGoogleReal: number | null;
  roasMetaRealKits: number;
  roasGoogleRealKits: number;
  roasMetaRealHt: number;
  roasGoogleRealHt: number;
  roasPayback: number | null;
  roasBlended: number | null;
  /** Numérateurs des ROAS, HT, pour les infobulles. */
  roasKitsHt: number;
  roasNewSubscribersMrrHt: number | null;
  /** Kits vendus grâce à la pub (Shopify + Amazon, sans BtoB) × LTV HT par acheteur. */
  roasKitsLtvHt: number | null;
  roasAdKitsSold: number;
  /** MC2 sans / avec abonnements, HT : montant, taux, et ce qui manque. */
  mc2: SummaryMc2 | null;
  mc2WithSubscriptions: SummaryMc2 | null;
  /** MC2 avec abo − branding de la fenêtre ; `null` sans MC2 ou sans branding chargé. */
  mc3: { margin: number; percent: number | null; branding: number } | null;
  /** MC3 − charges fixes de la fenêtre ; `null` sans MC3 ou sans charges chargées. */
  mc4: { margin: number; percent: number | null; fixedCharges: number } | null;
  /** L'entonnoir CA HT → MC4, abonnements compris ; `null` tant que la MC2 avec abo manque. */
  funnel: FunnelStep[] | null;
  /** Kits sans période de coût / pianos sans coût Shopify : MC2 non calculée tant qu'il en reste. */
  kitsWithoutCost: number;
  pianosWithoutCost: number;
  /** CA produits (hors abonnements) TTC, et moins les dépenses pub. */
  productsTtc: number;
  productsHt: number;
  productsAfterAds: number | null;
}

export interface SummaryMc2 {
  revenueHt: number;
  cogs: number;
  /** Frais Shopify Payments sur la part Shopify. */
  paymentFees: number;
  adSpend: number;
  /** Cash d'abonnements de la fenêtre, HT, avant retenues (`0` sans abo). */
  subscriptionsHt: number;
  /** Chaque retenue (Sacem, Fees Stripe…) sur ce cash, dans l'ordre de la page Souscriptions. */
  subscriptionDeductions: Array<{ label: string; amount: number }>;
  /** `subscriptionsHt` − les retenues : ce que les abonnements ajoutent à la marge. */
  subscriptionsFinalHt: number;
  margin: number;
  /** `margin ÷ revenueHt`, en %. `null` sans CA. */
  percent: number | null;
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const VAT = 1 + SUBSCRIPTION_VAT_PERCENT / 100;

/**
 * Un nombre, ou `null`.
 *
 * Garde-fou contre une réponse d'une version antérieure de l'API, gardée en
 * mémoire par le navigateur après un déploiement : un champ absent donnerait
 * `NaN` dans une addition, et « NaN € » dans une tuile. Mieux vaut un tiret.
 */
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const sum = (...values: Array<number | null>): number | null =>
  values.some((value) => value === null) ? null : round2(values.reduce<number>((acc, value) => acc + (value ?? 0), 0));
const ratio = (numerator: number | null, denominator: number | null | undefined): number | null =>
  numerator !== null && denominator != null && denominator > 0 ? round2(numerator / denominator) : null;

export function summaryMetrics({
  shop,
  subscriptions,
  ads,
  amazon,
  amazonAds,
  btob,
  googleAds,
  ltv,
  shopPaymentFee,
  subscriptionDeductions = [],
  fixedCharges,
  branding,
  shippingCostPerOrder,
}: SummaryInputs): SummaryMetrics | null {
  if (!shop) return null;
  const categories = shop.categories;
  const cat = (key: keyof typeof categories) => ({
    ttc: num(categories?.[key]?.netRevenue) ?? 0,
    gross: num(categories?.[key]?.grossRevenue) ?? 0,
    ht: num(categories?.[key]?.netRevenueExclTax) ?? 0,
    units: num(categories?.[key]?.units) ?? 0,
    cogs: num(categories?.[key]?.cogs) ?? 0,
    cogsMissingUnits: num(categories?.[key]?.cogsMissingUnits) ?? 0,
    paidUnits: num(categories?.[key]?.paidUnits) ?? 0,
  });

  const amazonTtc = num(amazon?.revenue) ?? 0;
  const amazonHt = num(amazon?.revenueExclTax) ?? 0;
  const amazonKitsTtc = num(amazon?.kitsRevenue) ?? 0;
  const amazonKitsHt = num(amazon?.kitsRevenueExclTax) ?? 0;
  const btobTtc = num(btob?.revenueTtc) ?? 0;
  const btobHt = num(btob?.revenueHt) ?? 0;
  // Abonnements vendus sur le site : dans le CA Shopify ET dans le CA abonnements.
  const siteTtc = num(subscriptions?.siteRevenue) ?? 0;
  const siteHt = round2(siteTtc / VAT);
  const shopTtc = sum(num(shop.netRevenue), -siteTtc, amazonTtc, btobTtc);
  const shopHt = sum(num(shop.netRevenueExclTax), -siteHt, amazonHt, btobHt);
  const subsTtc = subscriptions ? num(subscriptions.netRevenue) : null;
  const subsHt = subscriptions ? num(subscriptions.netRevenueExclTax) : null;
  const totalTtc = sum(shopTtc, subsTtc);
  const totalHt = sum(shopHt, subsHt);

  const kits = { ttc: round2(cat('kit').ttc + amazonKitsTtc + btobTtc), ht: round2(cat('kit').ht + amazonKitsHt + btobHt) };
  const pianos = { ttc: cat('piano').ttc, ht: cat('piano').ht };
  const subscriptionsCategory = subsTtc !== null && subsHt !== null ? { ttc: subsTtc, ht: subsHt } : null;
  // Le reste : accessoires, port, Amazon hors kits — ce que le total contient et que les trois autres n'ont pas.
  const others = {
    ttc: totalTtc !== null ? round2(totalTtc - kits.ttc - pianos.ttc - (subsTtc ?? 0)) : round2(cat('other').ttc + amazonTtc - amazonKitsTtc),
    ht: totalHt !== null ? round2(totalHt - kits.ht - pianos.ht - (subsHt ?? 0)) : round2(cat('other').ht + amazonHt - amazonKitsHt),
  };

  const metaSpend = num(ads?.spend);
  const googleSpend = num(googleAds?.spend);
  const spend = metaSpend !== null ? round2(metaSpend + (num(amazonAds?.spend) ?? 0) + (googleSpend ?? 0)) : null;

  // ROAS par régie : kits attribués × prix HT moyen d'un kit Shopify ÷ dépense de la régie.
  const kitPriceHt = cat('kit').units > 0 ? round2(cat('kit').ht / cat('kit').units) : null;
  const metaKits = num(ads?.metaPurchases) ?? 0;
  const googleKits = num(googleAds?.attributedOrders) ?? 0;
  const platformRoas = (kitsAttributed: number, platformSpend: number | null) =>
    kitPriceHt !== null ? ratio(round2(kitsAttributed * kitPriceHt), platformSpend) : null;

  // ROAS : kits payants vendus grâce à la pub (Shopify + Amazon), HT.
  const roasKitsHt = round2(cat('kit').ht + amazonKitsHt);
  const newSubscribersMrrHt = subscriptions ? ratio(num(subscriptions.newSubscribersMrr), VAT) : null;
  const adKitsSold = cat('kit').units + (num(amazon?.kits) ?? 0);
  // CPA : seuls les Piano LED payés comptent (kits à 0 € — SAV, offerts — exclus), sans BtoB.
  const cpaPaidKits = cat('kit').paidUnits + (num(amazon?.paidKits) ?? 0);
  const kitsLtvHt = ltv?.valueExclTax != null ? round2(adKitsSold * ltv.valueExclTax) : null;

  // MC2 : kits (Shopify + Amazon) et pianos, au coût de leur période.
  const kitsWithoutCost = cat('kit').cogsMissingUnits + (num(amazon?.kitsWithoutCost) ?? 0);
  const pianosWithoutCost = cat('piano').cogsMissingUnits;
  const mc2Of = (withSubscriptions: boolean): SummaryMc2 | null => {
    if (spend === null || kitsWithoutCost > 0 || pianosWithoutCost > 0 || !shopPaymentFee) return null;
    if (withSubscriptions && (!subscriptions || subsHt === null || subsTtc === null)) return null;
    const shopHtPart = cat('kit').ht + cat('piano').ht;
    const shopGrossPart = cat('kit').gross + cat('piano').gross;
    const productsHt = round2(shopHtPart + amazonKitsHt);
    const cogs = round2(cat('kit').cogs + cat('piano').cogs + (num(amazon?.kitsCogs) ?? 0));
    // Part fixe par commande au prorata de la part des kits + pianos dans le TTC encaissé.
    const shopGross = num(shop.grossRevenue) ?? 0;
    const orders = shopGross > 0 ? ((num(shop.orders) ?? 0) * shopGrossPart) / shopGross : 0;
    const paymentFees = shopPaymentFeeOf(shopPaymentFee, { grossRevenue: shopGrossPart, orders });
    // Retenues sur le CASH d'abonnements de la fenêtre (pas sur le MRR, qui est
    // un chiffre mensuel) : Sacem 8 % du HT, Fees Stripe 3 % du HT…
    const subscriptionsHt = withSubscriptions ? (subsHt ?? 0) : 0;
    const deductions = withSubscriptions
      ? subscriptionDeductions.map((deduction) => ({
          label: deduction.label,
          amount: round2((((deduction.base === 'ht' ? subsHt : subsTtc) ?? 0) * deduction.percent) / 100),
        }))
      : [];
    const subsFinal = round2(subscriptionsHt - deductions.reduce((sum, deduction) => sum + deduction.amount, 0));
    const revenueHt = round2(productsHt + subscriptionsHt);
    const margin = round2(productsHt - cogs - paymentFees - spend + subsFinal);
    return {
      revenueHt,
      cogs,
      paymentFees,
      adSpend: spend,
      subscriptionsHt,
      subscriptionDeductions: deductions,
      subscriptionsFinalHt: subsFinal,
      margin,
      percent: revenueHt > 0 ? round2((margin / revenueHt) * 100) : null,
    };
  };

  return {
    totalTtc,
    totalHt,
    kits,
    pianos,
    subscriptions: subscriptionsCategory,
    others,
    kitsSold: cat('kit').units + (num(amazon?.kits) ?? 0) + (num(btob?.units) ?? 0),
    pianosSold: cat('piano').units,
    siteSubscriptionsSold: num(subscriptions?.siteUnits) ?? cat('subscription').units,
    spendTotal: spend,
    roasGlobal: ratio(roasKitsHt, spend),
    cpa: ratio(spend, cpaPaidKits),
    cpaPaidKits,
    roasMeta: platformRoas(metaKits, metaSpend),
    roasGoogle: platformRoas(googleKits, googleSpend),
    roasMetaReal: ratio(num(shop.adClicks?.meta?.revenueExclTax) ?? 0, metaSpend),
    roasGoogleReal: ratio(num(shop.adClicks?.google?.revenueExclTax) ?? 0, googleSpend),
    roasMetaRealKits: num(shop.adClicks?.meta?.kits) ?? 0,
    roasGoogleRealKits: num(shop.adClicks?.google?.kits) ?? 0,
    roasMetaRealHt: num(shop.adClicks?.meta?.revenueExclTax) ?? 0,
    roasGoogleRealHt: num(shop.adClicks?.google?.revenueExclTax) ?? 0,
    roasMetaKits: metaKits,
    roasGoogleKits: googleKits,
    roasKitPriceHt: kitPriceHt,
    metaSpend,
    googleSpend,
    roasPayback: ratio(sum(roasKitsHt, newSubscribersMrrHt), spend),
    roasBlended: ratio(sum(roasKitsHt, kitsLtvHt), spend),
    roasKitsHt,
    roasNewSubscribersMrrHt: newSubscribersMrrHt,
    roasKitsLtvHt: kitsLtvHt,
    roasAdKitsSold: adKitsSold,
    mc2: mc2Of(false),
    mc2WithSubscriptions: mc2Of(true),
    ...(() => {
      const withSubscriptions = mc2Of(true);
      const pct = (margin: number) => (withSubscriptions && withSubscriptions.revenueHt > 0 ? round2((margin / withSubscriptions.revenueHt) * 100) : null);
      const mc3 = withSubscriptions && branding !== undefined ? { margin: round2(withSubscriptions.margin - branding), branding } : null;
      const mc4 = mc3 && fixedCharges !== undefined ? { margin: round2(mc3.margin - fixedCharges), fixedCharges } : null;
      // Entonnoir : MC1 = MC2 + pub (la pub est la seule différence) ; MC1' retire
      // le port des commandes kits + pianos, au prorata comme les frais fixes.
      let funnel: FunnelStep[] | null = null;
      if (withSubscriptions) {
        const revenueHt = withSubscriptions.revenueHt;
        const mc1 = round2(withSubscriptions.margin + withSubscriptions.adSpend);
        const shopGross = num(shop.grossRevenue) ?? 0;
        const shopGrossPart = cat('kit').gross + cat('piano').gross;
        const orders = shopGross > 0 ? ((num(shop.orders) ?? 0) * shopGrossPart) / shopGross : 0;
        const mc1Prime = shippingCostPerOrder != null ? round2(mc1 - orders * shippingCostPerOrder) : null;
        const step = (key: FunnelStep['key'], label: string, value: number | null): FunnelStep => ({
          key,
          label,
          value,
          percent: value !== null && revenueHt > 0 ? round2((value / revenueHt) * 100) : null,
        });
        funnel = [
          step('revenue', 'CA HT', revenueHt),
          step('mc1', 'MC1', mc1),
          step('mc1Prime', "MC1'", mc1Prime),
          step('mc2', 'MC2', withSubscriptions.margin),
          step('mc3', 'MC3', mc3?.margin ?? null),
          step('mc4', 'MC4', mc4?.margin ?? null),
        ];
      }
      return {
        mc3: mc3 ? { ...mc3, percent: pct(mc3.margin) } : null,
        mc4: mc4 ? { ...mc4, percent: pct(mc4.margin) } : null,
        funnel,
      };
    })(),
    kitsWithoutCost,
    pianosWithoutCost,
    productsTtc: shopTtc ?? 0,
    productsHt: shopHt ?? 0,
    productsAfterAds: shopTtc !== null && spend !== null ? round2(shopTtc - spend) : null,
  };
}
